import React, { useState, useEffect, useContext } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, Calendar, AlertTriangle, CheckCircle, Clock, Loader2, 
  RefreshCw, TrendingUp, Brain, Sparkles, Download, Filter,
  ChevronDown, ChevronUp, Zap, FileSearch, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

export const ReportsPage = () => {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [period, setPeriod] = useState('weekly');
  const [actionItems, setActionItems] = useState(null);
  const [actionFilter, setActionFilter] = useState('all');
  const [showActionItems, setShowActionItems] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/ai-summary?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadActionItems = async () => {
    try {
      const response = await axios.get(`${API}/reports/action-items?status=${actionFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActionItems(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadReport();
  }, [period]);

  useEffect(() => {
    if (showActionItems) {
      loadActionItems();
    }
  }, [showActionItems, actionFilter]);

  const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-${color}-500/10 border border-${color}-500/20 flex items-center justify-center`}>
            <Icon size={20} className={`text-${color}-400`} />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{value}</p>
            <p className="text-xs text-slate-400">{label}</p>
            {subtext && <p className="text-xs text-slate-500 mt-0.5">{subtext}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-8 bg-slate-950 min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight flex items-center gap-3">
            <Brain className="text-indigo-400" />
            AI Reports
          </h1>
          <p className="text-base text-slate-400 mt-2">AI-generated insights and analytics for your organization</p>
        </div>
        
        <div className="flex items-center gap-3 mt-4 md:mt-0">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36 bg-slate-900 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="weekly">Last 7 Days</SelectItem>
              <SelectItem value="monthly">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={loadReport}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <RefreshCw size={16} className="mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {loading && !report ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={40} className="animate-spin text-indigo-400 mx-auto mb-4" />
            <p className="text-slate-400">Generating AI Report...</p>
          </div>
        </div>
      ) : report ? (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard icon={FileText} label="New Documents" value={report.summary.new_documents} color="indigo" />
            <StatCard icon={AlertTriangle} label="Expiring Contracts" value={report.summary.expiring_contracts} color="amber" />
            <StatCard icon={CheckCircle} label="Action Items Due" value={report.summary.action_items_due} color="emerald" />
            <StatCard icon={Zap} label="Queries Made" value={report.summary.queries_made} color="purple" />
            <StatCard icon={TrendingUp} label="Usage" value={`${report.summary.usage_percent}%`} color="blue" subtext={`${report.usage?.queries_used || 0}/${report.usage?.queries_limit || 0}`} />
          </div>

          {/* AI Insights */}
          {report.ai_insights && (
            <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Sparkles className="text-indigo-400" size={20} />
                  AI Executive Summary
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Generated {new Date(report.generated_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose prose-invert prose-sm max-w-none text-slate-300">
                  <ReactMarkdown>{report.ai_insights}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Two Column Layout */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Expiring Contracts */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Calendar className="text-amber-400" size={20} />
                  Expiring Contracts
                  {report.expiring_contracts.length > 0 && (
                    <span className="ml-auto text-xs px-2 py-1 bg-amber-500/20 text-amber-300 rounded-full">
                      {report.expiring_contracts.length} upcoming
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.expiring_contracts.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">No contracts expiring in the next 60 days</p>
                ) : (
                  <div className="space-y-3">
                    {report.expiring_contracts.map((contract, idx) => (
                      <div key={idx} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                        <p className="text-sm font-medium text-white">{contract.title}</p>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            Expires: <span className="text-amber-400">{contract.expiration_date}</span>
                          </span>
                          <span>{contract.department}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* New Documents */}
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <FileText className="text-indigo-400" size={20} />
                  Recent Documents
                  {report.new_documents.length > 0 && (
                    <span className="ml-auto text-xs px-2 py-1 bg-indigo-500/20 text-indigo-300 rounded-full">
                      {report.new_documents.length} new
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.new_documents.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">No new documents in this period</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {report.new_documents.slice(0, 10).map((doc, idx) => (
                      <div key={idx} className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-white flex-1">{doc.title}</p>
                          {doc.is_contract && (
                            <span className="text-xs px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded">Contract</span>
                          )}
                        </div>
                        {doc.ai_summary && (
                          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{doc.ai_summary}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">{doc.department}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Action Items Section */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <CheckCircle className="text-emerald-400" size={20} />
                  Action Items Due Soon
                  {report.action_items_due.length > 0 && (
                    <span className="ml-2 text-xs px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded-full">
                      {report.action_items_due.length} items
                    </span>
                  )}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowActionItems(!showActionItems)}
                  className="text-slate-400 hover:text-white"
                >
                  {showActionItems ? 'Show Summary' : 'View All'}
                  {showActionItems ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!showActionItems ? (
                // Summary view
                report.action_items_due.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-4">No action items due in the next 14 days</p>
                ) : (
                  <div className="space-y-2">
                    {report.action_items_due.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                        <div className={`w-2 h-2 rounded-full ${
                          item.priority === 'high' ? 'bg-red-400' :
                          item.priority === 'medium' ? 'bg-amber-400' : 'bg-slate-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{item.task}</p>
                          <p className="text-xs text-slate-400">From: {item.document}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {item.deadline && (
                            <p className="text-xs text-amber-400">{item.deadline}</p>
                          )}
                          {item.assignee && (
                            <p className="text-xs text-slate-500">{item.assignee}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // Full action items view
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Select value={actionFilter} onValueChange={setActionFilter}>
                      <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                      </SelectContent>
                    </Select>
                    {actionItems && (
                      <span className="text-sm text-slate-400">
                        {actionItems.total} total • {actionItems.overdue_count} overdue
                      </span>
                    )}
                  </div>
                  
                  {actionItems?.action_items?.length === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-4">No action items found</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {actionItems?.action_items?.map((item, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border ${
                          item.is_overdue 
                            ? 'bg-red-500/10 border-red-500/30' 
                            : 'bg-slate-800/50 border-slate-700'
                        }`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-2 h-2 rounded-full mt-1.5 ${
                              item.priority === 'high' ? 'bg-red-400' :
                              item.priority === 'medium' ? 'bg-amber-400' : 'bg-slate-400'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white">{item.task}</p>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-400">
                                <span className="flex items-center gap-1">
                                  <FileText size={12} />
                                  {item.document_title}
                                </span>
                                {item.assignee && (
                                  <span className="flex items-center gap-1">
                                    <Users size={12} />
                                    {item.assignee}
                                  </span>
                                )}
                                {item.deadline && (
                                  <span className={`flex items-center gap-1 ${item.is_overdue ? 'text-red-400' : ''}`}>
                                    <Calendar size={12} />
                                    {item.deadline}
                                    {item.is_overdue && <span className="text-red-400 font-medium ml-1">OVERDUE</span>}
                                  </span>
                                )}
                                {item.category && (
                                  <span className="px-1.5 py-0.5 bg-slate-700 rounded">{item.category}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};

export default ReportsPage;
